import type { WizardStepKey } from './step';

/**
 * The reply draft tour runs as one short tour per wizard step, because a tour
 * can only highlight what is mounted when it starts, and the points do not
 * exist until the expert has picked a position.
 */
export type ReplyDraftTourSegment = 'decide' | 'review';

export type ReplyDraftTourPhase = 'idle' | ReplyDraftTourSegment | 'done';

/** `auto` is the expert's first draft, gated by the callout; `replay` is asked for, and writes nothing. */
export type ReplyDraftTourTrigger = 'auto' | 'replay';

type SegmentDefinition = {
  tourKey: string;
  step: WizardStepKey;
  next: ReplyDraftTourPhase;
};

export const REPLY_DRAFT_TOUR_SEGMENTS: Record<ReplyDraftTourSegment, SegmentDefinition> = {
  decide: { tourKey: 'reply-draft-decide', step: 'card', next: 'review' },
  review: { tourKey: 'reply-draft-review', step: 'review', next: 'done' },
};

/**
 * Each target's place in its segment. Zone 1 starts the segment, so it must be
 * a target every draft renders; a later zone a draft lacks, such as Research
 * without a brief, is skipped.
 */
export const REPLY_DRAFT_TOUR_ZONES = {
  decide: { steps: 1, research: 2, options: 3 },
  review: { points: 1, note: 2, useDraft: 3 },
} as const satisfies Record<ReplyDraftTourSegment, Record<string, number>>;

const SEGMENT_ORDER: readonly ReplyDraftTourSegment[] = ['decide', 'review'];

export function isTourSegment(phase: ReplyDraftTourPhase): phase is ReplyDraftTourSegment {
  return phase === 'decide' || phase === 'review';
}

export function segmentForStep(step: WizardStepKey): ReplyDraftTourSegment {
  return step === 'card' ? 'decide' : 'review';
}

/** Whether `segment` comes after `than` in the wizard. */
export function isLaterSegment(
  segment: ReplyDraftTourSegment,
  than: ReplyDraftTourSegment
): boolean {
  return SEGMENT_ORDER.indexOf(segment) > SEGMENT_ORDER.indexOf(than);
}

export function segmentForTourKey(tourKey: string | undefined): ReplyDraftTourSegment | undefined {
  return SEGMENT_ORDER.find((segment) => REPLY_DRAFT_TOUR_SEGMENTS[segment].tourKey === tourKey);
}

export function tourZoneKey(segment: ReplyDraftTourSegment, zone: number): string {
  return `${segment}:${zone}`;
}
